import React, { useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";
import { useInvoiceData } from "../../../hooks/useInvoiceData";
import { useReactToPrint } from "react-to-print";
import InvoicePreview from "./InvoicePreview";
import { InvoiceStatus } from "enums/InvoiceStatus";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useGetInvoiceStatusDetails from "hooks/useGetInvoiceStatusDetails";
import {
  EditInvoiceConfirmationModal,
  SendInvoiceAsEstimateConfirmationModal,
  SendInvoiceConfirmationModal,
} from "./InvoiceModules";
import { FormikInvoice } from "utils/invoices/invoiceFormat";
import useCanTakePayments from "hooks/useCanTakePayments";

interface InvoicePreviewContainerProps {
  invoice?: FormikInvoice;
  invoiceId?: string;
  open: boolean;
  onExit: () => void;
}

const InvoicePreviewContainer = (props: InvoicePreviewContainerProps) => {
  // An invoice email asks the customer to pay, so sending one waits on the company being able to take payments.
  const canTakePayments = useCanTakePayments();
  const { invoiceId, open, onExit } = props;
  const { t } = useTranslation();
  const [isPrintLoading, setIsPrintLoading] = React.useState(false);
  const [openEditSentInvoiceModal, setOpenEditSentInvoiceModal] =
    React.useState(false);
  const [
    openSendInvoiceConfirmationModal,
    setOpenSendInvoiceConfirmationModal,
  ] = React.useState(false);
  const [openSendInvoiceAsEstimateModal, setOpenSendInvoiceAsEstimateModal] =
    React.useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const {
    formikInvoice,
    specificLoadingStates,
    handleOpenEditInvoicePage,
    handleOpenInvoiceHistoryPage,
  } = useInvoiceData({
    invoiceId: invoiceId,
    shouldFetchAttachments: false,
    shouldNotQueryInvoice: !!props.invoice,
  });

  const invoice = props.invoice || formikInvoice.values;

  const { isInvoiceEditable, isInvoiceSendable, isInvoiceSent, isEstimate } =
    useGetInvoiceStatusDetails(invoice.status as InvoiceStatus);

  const handleAfterPrint = useCallback(() => {
    setIsPrintLoading(false);
  }, []);

  const handleBeforePrint = useCallback(() => {
    setIsPrintLoading(true);
    return Promise.resolve();
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Invoice",
    onBeforePrint: handleBeforePrint,
    onAfterPrint: handleAfterPrint,
  });

  const onEditPage = location.pathname.includes(
    "/admin/invoices/send-an-invoice",
  );

  const shouldNavigateToInvoices = () => {
    if (onEditPage && invoice.id) {
      handleOpenInvoiceHistoryPage(invoice.id);
    }
  };

  return (
    <Dialog open={open} onClose={onExit} maxWidth="md" fullWidth>
      <DialogTitle>
        <Inline wrap={false} justify="between" align="center" gap="0">
          <Typography variant="h4">{t("Invoice Preview")}</Typography>
          <IconButton
            size="small"
            variant="ghost"
            aria-label={t("Close")}
            onClick={onExit}
          >
            <HugeiconsIcon icon={Cancel01Icon} size={20} />
          </IconButton>
        </Inline>
      </DialogTitle>

      <DialogContent>
        <Box ref={printRef}>
          {specificLoadingStates.populatingInvoice ? (
            <Box
              sx={{
                width: "210mm",
                minHeight: "297mm",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body1">
                {t("LoadingInvoicePreview")}
              </Typography>
            </Box>
          ) : (
            <InvoicePreview invoice={invoice} />
          )}
        </Box>
      </DialogContent>

      {invoice.id && (
        <DialogActions>
          {/* The page's other action rows: utilities first, the sends on the right with the primary last. */}
          <Inline justify="between" gap="var(--space-3)" sx={{ width: "100%" }}>
            <Inline gap="var(--space-3)">
              <Button
                variant="default-outlined"
                onClick={() => {
                  handlePrint();
                }}
                disabled={isPrintLoading}
              >
                {t("Print")}
              </Button>

              {!onEditPage && isInvoiceEditable && (
                <Button
                  variant="default-outlined"
                  onClick={() => {
                    if (isInvoiceSent) {
                      setOpenEditSentInvoiceModal(true);
                    } else {
                      invoice?.id && handleOpenEditInvoicePage(invoice.id);
                    }
                  }}
                >
                  {t("Edit")}
                </Button>
              )}
            </Inline>

            {isInvoiceSendable && (
              <Inline gap="var(--space-3)">
                <Button
                  variant="default-outlined"
                  onClick={async () => {
                    if (invoice.id) {
                      setOpenSendInvoiceAsEstimateModal(true);
                    }
                  }}
                >
                  {isEstimate ? t("Resend Estimate") : t("Send as Estimate")}
                </Button>

                {canTakePayments && (
                  <Button
                    variant="primary-filled"
                    onClick={async () => {
                      if (invoice.id) {
                        setOpenSendInvoiceConfirmationModal(true);
                      }
                    }}
                  >
                    {isInvoiceSent ? t("Resend Invoice") : t("Send Invoice")}
                  </Button>
                )}
              </Inline>
            )}
          </Inline>

          <EditInvoiceConfirmationModal
            openEditSentInvoiceModal={openEditSentInvoiceModal}
            selectedInvoiceId={invoice?.id}
            onCloseModal={() => setOpenEditSentInvoiceModal(false)}
          />

          <SendInvoiceConfirmationModal
            openSendInvoiceConfirmationModal={openSendInvoiceConfirmationModal}
            invoice={invoice}
            onCloseModal={(selectedYes) => {
              setOpenSendInvoiceConfirmationModal(false);
              if (selectedYes) {
                shouldNavigateToInvoices();
              }
            }}
          />

          <SendInvoiceAsEstimateConfirmationModal
            openSendInvoiceAsEstimateModal={openSendInvoiceAsEstimateModal}
            invoice={invoice}
            onCloseModal={(selectedYes) => {
              setOpenSendInvoiceAsEstimateModal(false);
              if (selectedYes) {
                shouldNavigateToInvoices();
              }
            }}
          />
        </DialogActions>
      )}
    </Dialog>
  );
};

export default InvoicePreviewContainer;
